/** Buyer Ledger Creation — shared constants and pure helpers */

export const MAX_SHIPPING_TABS = 5;

export const SUBMASTER_TYPES = [
  { key: 'currency', label: 'Currency' },
  { key: 'ingo-term', label: 'INGO Term' },
  { key: 'agency', label: 'Agency' },
  { key: 'payment-term', label: 'Payment Term' },
  { key: 'barcode-type', label: 'Barcode Type' },
];

export const emptyShippingRow = () => ({
  distributionCenter: '',
  placeOfReceipt: '',
  consigneeName: '',
  consigneeAddress: '',
  shippingName: '',
  shippingAddress: '',
});

export const mapApiShippingRow = (row) => ({
  distributionCenter: row.distributionCenter ?? row.distribution_center ?? '',
  placeOfReceipt: row.placeOfReceipt ?? row.place_of_receipt ?? '',
  consigneeName: row.consigneeName ?? row.consignee_name ?? '',
  consigneeAddress: row.consigneeAddress ?? row.consignee_address ?? '',
  shippingName: row.shippingName ?? row.shipping_name ?? '',
  shippingAddress: row.shippingAddress ?? row.shipping_address ?? '',
});

export const INITIAL_FORM = {
  currency: '',
  ingoTerm: '',
  clientTo: '',
  agency: '',
  paymentTerm: '',
  barcodeType: '',
  name: '',
  clientType: 'Domestic',
  clientId: '',
  contactAddress: '',
  contactCommercial: '',
  billingContact: '',
  email: '',
  shippingAddresses: [emptyShippingRow()],
  gstNo: '',
  tan: '',
  lut: '',
  notifyParty: '',
  alsoNotifyParty: '',
  portOfDelivery: '',
  countryOfReceipt: '',
};

export const SHIPPING_ADDRESS_FIELDS = [
  { key: 'distributionCenter', label: 'Distribution Center / Warehouse', placeholder: 'Enter distribution center code' },
  { key: 'placeOfReceipt', label: 'Place of Receipt', placeholder: 'Enter place of receipt' },
  { key: 'consigneeName', label: 'Consignee Name', placeholder: 'Enter consignee name' },
  { key: 'consigneeAddress', label: 'Consignee Address', placeholder: 'Enter consignee address' },
  { key: 'shippingName', label: 'Shipping Name', placeholder: 'Enter shipping name' },
  { key: 'shippingAddress', label: 'Shipping Address', placeholder: 'Enter shipping address' },
];

/**
 * Maps API submaster type key to form field name.
 */
export function mapKeyToField(key) {
  switch (key) {
    case 'currency':
      return 'currency';
    case 'ingo-term':
      return 'ingoTerm';
    case 'agency':
      return 'agency';
    case 'payment-term':
      return 'paymentTerm';
    case 'barcode-type':
      return 'barcodeType';
    default:
      return key.replace(/-/g, '_');
  }
}

/**
 * Maps loaded client API object to Buyer Ledger form state (edit mode).
 */
export function mapClientToForm(client) {
  const listRaw = client.shippingAddresses || client.shipping_addresses;
  let shippingAddresses;
  if (Array.isArray(listRaw) && listRaw.length > 0) {
    shippingAddresses = listRaw.map(mapApiShippingRow);
  } else {
    shippingAddresses = [emptyShippingRow()];
    shippingAddresses[0] = {
      distributionCenter: client.distributionCenter ?? client.distribution_center ?? '',
      placeOfReceipt: client.placeOfReceipt ?? client.place_of_receipt ?? '',
      consigneeName: client.consigneeName ?? client.consignee_name ?? '',
      consigneeAddress: client.consigneeAddress ?? client.consignee_address ?? '',
      shippingName: client.shippingName ?? client.shipping_name ?? '',
      shippingAddress: client.shippingAddress ?? client.shipping_address ?? '',
    };
  }

  return {
    ...INITIAL_FORM,
    currency: client.currency_id || client.currency?.id || '',
    ingoTerm: client.ingo_term_id || client.ingoTerm?.id || '',
    clientTo: client.client_to_id || client.clientTo?.id || '',
    agency: client.agency_id || client.agency?.id || '',
    paymentTerm: client.payment_term_id || client.paymentTerm?.id || '',
    barcodeType: client.barcode_type_id || client.barcodeType?.id || '',
    name: client.name || '',
    clientType: client.clientType || client.client_type || 'Domestic',
    clientId: client.clientId || client.client_id || '',
    contactAddress: client.contactAddress ?? client.contact_address ?? '',
    contactCommercial: client.contactCommercial ?? client.contact_commercial ?? '',
    billingContact: client.billingContact ?? client.billing_contact ?? '',
    email: client.email || '',
    shippingAddresses,
    gstNo: client.gstNo ?? client.gst_no ?? '',
    tan: client.tan || '',
    lut: client.lut || '',
    notifyParty: client.notifyParty ?? client.notify_party ?? '',
    alsoNotifyParty: client.alsoNotifyParty ?? client.also_notify_party ?? '',
    portOfDelivery: client.portOfDelivery ?? client.port_of_delivery ?? '',
    countryOfReceipt: client.countryOfReceipt ?? client.country_of_receipt ?? '',
    _id: client.id,
  };
}

/**
 * Client-side validation for Buyer Ledger form.
 */
export function validateBuyerLedgerForm(form) {
  const errs = {};

  if (!form.clientId || !form.clientId.trim()) errs.clientId = 'Client Code is required';
  if (!form.name || !form.name.trim()) errs.name = 'Client Name is required';

  if (!form.clientTo) errs.clientTo = 'Company / Branch is required';
  if (!form.currency) errs.currency = 'Currency is required';
  if (!form.ingoTerm) errs.ingoTerm = 'INGO Term is required';
  if (!form.agency) errs.agency = 'Agency is required';
  if (!form.paymentTerm) errs.paymentTerm = 'Payment Term is required';
  if (!form.barcodeType) errs.barcodeType = 'Barcode Type is required';

  if (!form.contactAddress || !form.contactAddress.trim()) errs.contactAddress = 'Address is required';
  if (!form.email || !form.email.trim()) errs.email = 'Email is required';
  if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) errs.email = 'Enter a valid email address';
  if (!form.billingContact || !form.billingContact.trim()) errs.billingContact = 'Billing Contact is required';

  if (form.clientType === 'Domestic') {
    if (!form.gstNo || !form.gstNo.trim()) errs.gstNo = 'GST No. is required for Domestic clients';
  } else if (form.clientType === 'Export') {
    if (!form.notifyParty || !form.notifyParty.trim()) errs.notifyParty = 'Notify Party is required for Export clients';
    if (!form.portOfDelivery || !form.portOfDelivery.trim()) errs.portOfDelivery = 'Port of Delivery is required for Export clients';
    if (!form.countryOfReceipt) errs.countryOfReceipt = 'Country of Receipt is required for Export clients';
  }

  return errs;
}

/**
 * Build API payload (snake_case) from form state.
 */
export function buildClientSavePayload(form) {
  const shipping_addresses = (form.shippingAddresses || []).slice(0, MAX_SHIPPING_TABS).map((row) => ({
    distribution_center: row.distributionCenter || '',
    place_of_receipt: row.placeOfReceipt || '',
    consignee_name: row.consigneeName || '',
    consignee_address: row.consigneeAddress || '',
    shipping_name: row.shippingName || '',
    shipping_address: row.shippingAddress || '',
  }));

  return {
    currency_id: form.currency,
    ingo_term_id: form.ingoTerm,
    client_to_id: form.clientTo,
    agency_id: form.agency,
    payment_term_id: form.paymentTerm,
    barcode_type_id: form.barcodeType,
    client_id: form.clientId,
    name: form.name,
    client_type: form.clientType,
    contact_address: form.contactAddress,
    contact_commercial: form.contactCommercial,
    billing_contact: form.billingContact,
    email: form.email,
    shipping_addresses,
    gst_no: form.gstNo,
    tan: form.tan,
    lut: form.lut,
    notify_party: form.notifyParty,
    also_notify_party: form.alsoNotifyParty,
    port_of_delivery: form.portOfDelivery,
    country_of_receipt: form.countryOfReceipt,
  };
}
